import React from 'react';
import { FormInstance } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/fa';
import i18next from 'i18next';
import { LOCALES_CODES } from 'configs/constants';
import { DepartureArrivalValidationContext } from './types';
import type { RuleObject } from 'antd/es/form';

interface ValidationContext {
  form: FormInstance;
  t: (key: string) => string;
}
interface DatePickerInfo {
  type: string;
  originNode: React.ReactNode;
}

export const formatOtpTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  return `${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
};

/**
 * Disables dates after the previous day.
 *
 * @param {dayjs.Dayjs} current - The date to be checked.
 * @returns {boolean} - True if the date should be disabled, false otherwise.
 */
export const isDateAfterYesterday = (current: dayjs.Dayjs) => {
  return current && current.isAfter(dayjs().subtract(1, 'day'), 'day');
};

/**
 * Disables dates before today.
 *
 * @param {dayjs.Dayjs} current - The date to be checked.
 * @returns {boolean} - True if the date should be disabled, false otherwise.
 */
export const isDateBeforeToday = (current: dayjs.Dayjs) => {
  return current && current.isBefore(dayjs().startOf('day'), 'day');
};

export const disabledIssueDate = (current: dayjs.Dayjs) => {
  return current && current > dayjs().endOf('day');
};

export const disabledExpiryDate = (form: FormInstance, current: dayjs.Dayjs) => {
  const issueDate = form.getFieldValue('issueDate');
  return current && current <= dayjs(issueDate).endOf('day');
};

export const validateIssueDate = ({ form, t }: ValidationContext): RuleObject => ({
  validator(_: unknown, value: dayjs.Dayjs | null): Promise<void> {
    if (!value) {
      return Promise.resolve();
    }

    const expiryDate = form.getFieldValue('expiryDate');

    if (value && expiryDate && value.isAfter(expiryDate)) {
      return Promise.reject(
        new Error(t('traveler.details.documentsSection.validation.issueDateGreaterThanExpiryDate')),
      );
    }

    return Promise.resolve();
  },
});

export const validateExpiryDate = ({ form, t }: ValidationContext): RuleObject => ({
  validator(_: unknown, value: dayjs.Dayjs | null): Promise<void> {
    if (!value) {
      return Promise.resolve();
    }

    const issueDate = form.getFieldValue('issueDate');
    const currentDate = dayjs().startOf('day');

    if (value.isBefore(currentDate)) {
      return Promise.reject(new Error(t('traveler.details.documentsSection.validation.expiryDateBeforeCurrentDate')));
    }

    if (value && issueDate && value.isBefore(issueDate)) {
      return Promise.reject(new Error(t('traveler.details.documentsSection.validation.expiryDateLessThanIssueDate')));
    }

    return Promise.resolve();
  },
});

export const validateDepartureBeforeArrival = ({
  getFieldValue,
  t,
}: DepartureArrivalValidationContext): RuleObject => ({
  validator(_: unknown, value: unknown): Promise<void> {
    if (!value) {
      return Promise.resolve();
    }

    const departureDate = getFieldValue('departureDate');
    const departureTime = getFieldValue('departureTime');
    const arrivalDate = getFieldValue('arrivalDate');
    const arrivalTime = getFieldValue('arrivalTime');

    if (departureDate && departureTime && arrivalDate && arrivalTime) {
      const depDate = departureDate as dayjs.Dayjs;
      const depTime = departureTime as dayjs.Dayjs;
      const arrDate = arrivalDate as dayjs.Dayjs;
      const arrTime = arrivalTime as dayjs.Dayjs;

      const depDateTime = depDate.clone().hour(depTime.hour()).minute(depTime.minute()).second(0);
      const arrDateTime = arrDate.clone().hour(arrTime.hour()).minute(arrTime.minute()).second(0);

      if (depDateTime.isAfter(arrDateTime) || depDateTime.isSame(arrDateTime)) {
        return Promise.reject(new Error(t('flights.form.required.departureBeforeArrival')));
      }
    }

    return Promise.resolve();
  },
});

export const createNumberFormatter = (): Intl.NumberFormat => {
  return new Intl.NumberFormat(LOCALES_CODES[i18next.language as keyof typeof LOCALES_CODES]);
};

export const formatDateWithLocale = (date: dayjs.Dayjs | null, format: string): string => {
  if (!date) return '';

  // Format the date with the current locale
  const formattedDate = date.locale(i18next.language).format(format);

  if (i18next.language !== 'en') {
    const numberFormatter = createNumberFormatter();
    return formattedDate.replace(/[0-9]/g, (d) => numberFormatter.format(parseInt(d)));
  }
  return formattedDate;
};

export const renderDateWithLocale = (current: string | number | dayjs.Dayjs, info: DatePickerInfo): React.ReactNode => {
  // For non-date types, extract the appropriate value from the dayjs object
  if (info.type !== 'date') {
    return info.originNode;
  }

  const dateValue =
    typeof current === 'number' ? current : typeof current === 'string' ? parseInt(current, 10) : dayjs(current).date();

  if (i18next.language !== 'en') {
    const numberFormatter = createNumberFormatter();
    return React.createElement(
      'span',
      { className: info.type === 'date' ? 'ant-picker-cell-inner' : '' },
      numberFormatter.format(dateValue),
    );
  }
  return info.originNode;
};

export const formatTimeWithLocale = (time: dayjs.Dayjs | null, format: string): string => {
  if (!time) return '';

  // Format the time with the current locale
  const formattedTime = time.locale(i18next.language).format(format);

  if (i18next.language !== 'en') {
    const numberFormatter = createNumberFormatter();
    // Split the formatted time into hours and minutes
    const [hours, minutes] = formattedTime.split(':');
    const formattedHours = hours.replace(/[0-9]/g, (d) => numberFormatter.format(parseInt(d)));
    const formattedMinutes = minutes.replace(/[0-9]/g, (d) => numberFormatter.format(parseInt(d)));
    return `${formattedHours}:${formattedMinutes}`;
  }

  return formattedTime;
};

export const renderTimeWithLocale = (current: string | number | dayjs.Dayjs, info: DatePickerInfo): React.ReactNode => {
  if (info.type !== 'time') return info.originNode;

  const timeValue =
    typeof current === 'number' ? current : typeof current === 'string' ? parseInt(current, 10) : dayjs(current).hour();

  if (i18next.language !== 'en') {
    const numberFormatter = createNumberFormatter();
    return React.createElement(
      'span',
      { className: info.type === 'time' ? 'ant-picker-time-panel-cell-inner' : '' },
      numberFormatter.format(timeValue),
    );
  }
  return info.originNode;
};

export const getMonthYearParams = (date: Dayjs | null) => {
  if (!date) return { month: null, year: null };
  return { month: date.month() + 1, year: date.year() };
};

export const transformDatesToRangeRequest = (filters: { startDate: string; endDate: string }) => {
  const result = {
    fromMonth: null as number | null,
    toMonth: null as number | null,
    fromYear: null as number | null,
    toYear: null as number | null,
  };

  if (filters.startDate) {
    const startDate = new Date(filters.startDate);
    if (!isNaN(startDate.getTime())) {
      result.fromMonth = startDate.getMonth() + 1;
      result.fromYear = startDate.getFullYear();
    }
  }

  if (filters.endDate) {
    const endDate = new Date(filters.endDate);
    if (!isNaN(endDate.getTime())) {
      result.toMonth = endDate.getMonth() + 1;
      result.toYear = endDate.getFullYear();
    }
  }

  return result;
};
